/**
 * The overlay view's score-to-colour mapping.
 *
 * VOSviewer picks the range automatically from the **10th and 90th
 * percentiles** of the score — `OverlayColors.PROP_SCORES_BETWEEN_MIN_AND_MAX`
 * is 0.8, and the two ends are `(1 - 0.8) / 2` in from each side — and then
 * snaps both to round numbers with the tick algorithm.  Two things follow, and
 * both are deliberate:
 *
 * * a handful of outliers cannot flatten the map.  One item published in 1974
 *   in a corpus that is otherwise 2015-2024 would, on a min-max scale, push
 *   every real difference into the top 2% of the ramp.
 * * the ends of the ramp are saturated on purpose.  Roughly a fifth of the
 *   items sit at pure yellow or pure blue, and that is the point: the reader is
 *   meant to see *where* the extremes are, not to rank them against each other.
 *
 * Items with no value for the field are drawn in VOSviewer's grey
 * (191, 191, 191) rather than being hidden, so a sparse field is visible as
 * sparse.
 */

import type { ColorScheme, MapItem, RGB } from '../types';
import { NO_VALUE_COLOR, sampleScheme } from '../color/schemes';
import { niceMaxValue, niceMinValue, ticks, type Ticks } from '../color/ticks';
import { OVERLAY_DESIRED_TICKS, OVERLAY_SCORE_PROPORTION } from './constants';

export interface OverlayModel {
  readonly field: string;
  /** Per-item score; NaN where the item has no value for the field. */
  readonly score: Float64Array;
  readonly min: number;
  readonly max: number;
  /** Per-item RGB, three bytes each. */
  readonly rgb: Uint8Array;
  /** True where the item had no score, i.e. is drawn in the "no value" grey. */
  readonly missing: Uint8Array;
  readonly ticks: Ticks;
  readonly count: number;
}

export function scoreOf(item: MapItem, field: string): number {
  const scores = item.scores;
  if (!scores) return NaN;
  const v = scores[field];
  return typeof v === 'number' && Number.isFinite(v) ? v : NaN;
}

/**
 * `VOSviewer.calcAutoMinMaxScore`, including its rounding.
 *
 * The index is `round(p * (count - 1))` into the sorted scores, which is the
 * nearest-rank percentile rather than an interpolated one.  With fewer than
 * ten scored items the two ends can collide; the caller widens a degenerate
 * range so the ramp still has somewhere to go.
 */
export function autoRange(scores: Float64Array, count: number): [number, number] {
  if (count === 0) return [0, 0];
  const sorted = Array.from(scores.subarray(0, count)).sort((a, b) => a - b);
  const tail = (1 - OVERLAY_SCORE_PROPORTION) / 2;
  const lo = sorted[Math.round(tail * (count - 1))] ?? 0;
  const hi = sorted[Math.round((1 - tail) * (count - 1))] ?? 0;
  return [
    niceMinValue(lo, hi, OVERLAY_DESIRED_TICKS),
    niceMaxValue(lo, hi, OVERLAY_DESIRED_TICKS),
  ];
}

export function buildOverlay(
  items: readonly MapItem[],
  field: string,
  scheme: ColorScheme,
  noValue: RGB = NO_VALUE_COLOR,
): OverlayModel {
  const n = items.length;
  const score = new Float64Array(n);
  const missing = new Uint8Array(n);
  const present = new Float64Array(n);
  let count = 0;
  for (let i = 0; i < n; i += 1) {
    const v = scoreOf(items[i] as MapItem, field);
    score[i] = v;
    if (Number.isNaN(v)) {
      missing[i] = 1;
    } else {
      present[count] = v;
      count += 1;
    }
  }

  let [min, max] = autoRange(present, count);
  if (!(max > min)) {
    // Every score identical, or fewer than a handful of them: give the ramp a
    // symmetric unit of room so the map is a flat mid-scheme colour instead of
    // an arbitrary end of it.
    const centre = Number.isFinite(min) ? min : 0;
    min = centre - 0.5;
    max = centre + 0.5;
  }

  const rgb = new Uint8Array(n * 3);
  const span = max - min;
  for (let i = 0; i < n; i += 1) {
    let c: RGB;
    if (missing[i] === 1) {
      c = noValue;
    } else {
      const t = (Math.max(min, Math.min(max, score[i] as number)) - min) / span;
      c = sampleScheme(scheme, t);
    }
    rgb[i * 3] = c[0];
    rgb[i * 3 + 1] = c[1];
    rgb[i * 3 + 2] = c[2];
  }

  return {
    field,
    score,
    min,
    max,
    rgb,
    missing,
    ticks: ticks(min, max, OVERLAY_DESIRED_TICKS),
    count,
  };
}
